import { Component, computed, inject, input } from '@angular/core';
import { FeedbackInfoComponent } from 'ok-angular-shared/components';
import { DkCurrencyPipe } from '../../../../../shared/pipes/dk-currency';
import { AdmPriceFeedbackService } from './adm-price-feedback.service';
import { PriceFeedbackConfig } from './adm-price-feedback.interface';
import { PricePolicyType } from '../../interfaces/price-policy';

@Component({
  selector: 'adm-price-feedback',
  templateUrl: './adm-price-feedback.component.html',
  styleUrls: ['./adm-price-feedback.component.scss'],
  imports: [FeedbackInfoComponent],
})
export class AdmPriceFeedbackComponent {
  private readonly priceFeedbackService = inject(AdmPriceFeedbackService);
  private readonly currencyPipe = new DkCurrencyPipe();
  private isDynamic = computed(() => this.config().type === PricePolicyType.Dynamic);
  public pricePolicyType = PricePolicyType;

  config = input<PriceFeedbackConfig>({
    basePriceLow: null,
    basePriceHigh: null,
    basePriceFixed: null,
    okSupplement: null,
    organizationSupplement: null,
    type: PricePolicyType.FixedWithOkMeter,
  });

  // Calculate total supplements
  private totalSupplements = computed(() => {
    const { okSupplement, organizationSupplement } = this.config();
    const ok = okSupplement ?? 0;
    const org = organizationSupplement ?? 0;
    return ok + org;
  });

  // Calculate price excl. VAT - low (for dynamic) or fixed
  priceExclVatLow = computed(() => {
    const { basePriceLow, basePriceFixed } = this.config();
    if (this.isDynamic()) {
      const base = basePriceLow ?? 0;
      return base + this.totalSupplements();
    } else {
      const base = basePriceFixed ?? 0;
      return base + this.totalSupplements();
    }
  });

  // Calculate price excl. VAT - high (for dynamic only)
  priceExclVatHigh = computed(() => {
    const { basePriceHigh } = this.config();
    if (this.isDynamic()) {
      const base = basePriceHigh ?? 0;
      return base + this.totalSupplements();
    }
    return null;
  });

  // Calculate price incl. VAT - low
  priceInclVatLow = computed(() => {
    return this.priceFeedbackService.calculateInclVat(this.priceExclVatLow());
  });

  // Calculate price incl. VAT - high (for dynamic only)
  priceInclVatHigh = computed(() => {
    const high = this.priceExclVatHigh();
    return high !== null ? this.priceFeedbackService.calculateInclVat(high) : null;
  });

  // Formatted display strings
  displayExclVat = computed(() => {
    const low = this.currencyPipe.transform(this.priceExclVatLow());
    const high = this.priceExclVatHigh();

    if (this.isDynamic() && high !== null) {
      return `${low} - ${this.currencyPipe.transform(high)}`;
    }
    return low;
  });

  displayInclVat = computed(() => {
    const low = this.currencyPipe.transform(this.priceInclVatLow());
    const high = this.priceInclVatHigh();

    if (this.isDynamic() && high !== null) {
      return `${low} - ${this.currencyPipe.transform(high)}`;
    }
    return low;
  });
}
